<script setup>
import { defineAsyncComponent, ref, nextTick, onMounted } from 'vue'

// Dynamically load dropdown components
const StandartDropdown = defineAsyncComponent(() => 
  import('~/components/layout/header/desktop/dropdowns/standart.vue')
)

const ProductsDropdown = defineAsyncComponent(() => 
  import('~/components/layout/header/desktop/dropdowns/products.vue')
)

const DropdownColumns = defineAsyncComponent(() => 
  import('~/components/layout/header/desktop/dropdowns/dropdownColumns.vue')
)

// Define props
const props = defineProps({
  navItems: {
    type: Array,
    default: () => []
  },
})

// State variables
const selectedNav = ref(null)

// Refs for triggers and dropdown containers, keyed by item.id
const triggerRefs = ref({})
const dropdownRefs = ref({})

// Timeout for closing dropdowns
let closeTimeout = null

// -------------------------------------------------------------------------------------------------
// Helpers
// -------------------------------------------------------------------------------------------------

const getFocusableElements = (container) => {
  if (!container) return []
  return Array.from(
    container.querySelectorAll(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  )
}

const isElementInside = (root, target) => {
  if (!root || !target) return false
  if (!(target instanceof Node)) return false
  return root.contains(target)
}

// -------------------------------------------------------------------------------------------------
// Functions
// -------------------------------------------------------------------------------------------------

// Function to clear close timeout
const clearCloseTimeout = () => {
  if (closeTimeout) {
    clearTimeout(closeTimeout)
    closeTimeout = null
  }
}

// Inspect dropdown in DevTools: add ?debugDropdown=1 to the URL, open the dropdown, then move
// mouse away – it stays open ~30s. Or in Console run: setTimeout(() => { debugger; }, 2000);
// then open the dropdown; when the breakpoint hits, the dropdown stays open while paused.
const getCloseDelay = () => {
  if (typeof window === 'undefined') return 0
  return window.location.search.includes('debugDropdown=1') ? 30000 : 0
}

// Function to schedule close with delay (delay in ms; 0 = close immediately unless ?debugDropdown=1)
const scheduleClose = (delay = 100) => {
  clearCloseTimeout()
  const ms = delay === 0 ? getCloseDelay() : delay
  closeTimeout = setTimeout(() => {
    selectedNav.value = null
    closeTimeout = null
  }, ms)
}

// Function to handle nav item click
const handleNavClick = (item) => {
  clearCloseTimeout()
  if (selectedNav.value?.id === item.id) {
    // If clicking the same item, close it
    selectedNav.value = null
  } else {
    // Otherwise, select the new item
    selectedNav.value = item
  }
}

// Function to handle mouse enter
const handleMouseEnter = (item) => {
  clearCloseTimeout()
  if (item.children.length) {
    selectedNav.value = item
  }
}

// Function to handle mouse leave
const handleMouseLeave = () => {
  // With ?debugDropdown=1 in URL, delay 30s so you can inspect the dropdown in DevTools
  scheduleClose(getCloseDelay())
}

// Open dropdown and move focus to first item
const openDropdownAndFocusFirst = (item) => {
  selectedNav.value = item

  nextTick(() => {
    const dropdownEl = dropdownRefs.value[item.id]
    const focusable = getFocusableElements(dropdownEl)
    if (focusable.length) {
      focusable[0].focus()
    }
  })
}

// Handle keydown on trigger button
const handleButtonKeydown = (event, item) => {
  switch (event.key) {
    case 'Enter':
    case ' ':
      event.preventDefault()
      handleNavClick(item)
      break
    case 'ArrowDown':
      event.preventDefault()
      if (selectedNav.value?.id !== item.id) {
        openDropdownAndFocusFirst(item)
      } else {
        // If already open, move focus to first item
        const dropdownEl = dropdownRefs.value[item.id]
        const focusable = getFocusableElements(dropdownEl)
        if (focusable.length) {
          focusable[0].focus()
        }
      }
      break
    case 'Escape':
      if (selectedNav.value?.id === item.id) {
        event.preventDefault()
        selectedNav.value = null
      }
      break
  }
}

// Handle keydown inside dropdown (e.g., Escape to close)
const handleMenuKeydown = (event, item) => {
  if (event.key === 'Escape') {
    if (selectedNav.value?.id === item.id) {
      event.preventDefault()
      selectedNav.value = null

      // Return focus to trigger button
      const triggerEl = triggerRefs.value[item.id]
      if (triggerEl && typeof triggerEl.focus === 'function') {
        triggerEl.focus()
      }
    }
  }
}

// Handle focus leaving dropdown/trigger - close when focus moves outside
const handleFocusOut = (event, item) => {
  const nextTarget = event.relatedTarget
  const dropdownEl = dropdownRefs.value[item.id]
  const triggerEl = triggerRefs.value[item.id]

  // If focus moves within trigger or dropdown - keep open
  if (isElementInside(dropdownEl, nextTarget) || isElementInside(triggerEl, nextTarget)) {
    return
  }

  // Otherwise close the currently open dropdown for this item
  if (selectedNav.value?.id === item.id) {
    selectedNav.value = null
  }
}

// Function to handle dropdown mouse enter
const dropdownEnter = () => {
  clearCloseTimeout()
}

// Function to handle dropdown mouse leave
const dropdownLeave = (item) => {
  scheduleClose(100)
}

// After reload, the pointer may already sit over a nav item before listeners attach.
// mouseenter does not replay, so sync open state from the current :hover match.
const syncHoveredNavItem = () => {
  for (const item of props.navItems) {
    if (!item.children?.length) continue

    const navItemEl = document.getElementById(`header-nav-item-${item.id}`)
    if (navItemEl?.matches(':hover')) {
      handleMouseEnter(item)
      return
    }
  }
}

onMounted(() => {
  nextTick(syncHoveredNavItem)
})

</script>

<!-- -------------------------------------------------------------------------------------------------
    Template
------------------------------------------------------------------------------------------------- -->

<template>
  <nav role="navigation">
    <div v-for="(item, index) in navItems"
        class="top-nav-item"
        :class="{ 'has-dropdown': item.children.length }"
        :key="item.id"
        :id="`header-nav-item-${item.id}`"
        @mouseenter="handleMouseEnter(item)"
        @mouseleave="handleMouseLeave">

      <a v-if="!item.children.length"
        :href="item?.linkField?.url || '/'"
        class="link">
        <span class="text">{{ item.title }}</span>
      </a>

      <button v-else
        type="button"
        :aria-controls="`header-nav-dropdown-${item.id}`"
        :aria-expanded="selectedNav?.id === item.id ? 'true' : 'false'"
        :aria-label="`${item.title} menu`"
        :ref="(el) => { if (el) triggerRefs[item.id] = el }"
        @click="handleNavClick(item)"
        @keydown="(event) => handleButtonKeydown(event, item)"
      >
        <span class="text">{{ item.title }}</span>
        <span class="icon-caret" aria-hidden="true"></span>
      </button>


      <!-- v-if="selectedNav?.id === item.id" -->
      <!-- v-if="index === 1" -->
      <transition name="fade">
        <div class="dropdown-container"
          v-if="selectedNav?.id === item.id"
          :id="`header-nav-dropdown-${item.id}`"
          role="menu"
          :aria-labelledby="`header-nav-item-${item.id}`"
          :ref="(el) => { if (el) dropdownRefs[item.id] = el }"
          @mouseenter="dropdownEnter"
          @mouseleave="() => dropdownLeave(item)"
          @keydown="(event) => handleMenuKeydown(event, item)"
          @focusout="(event) => handleFocusOut(event, item)">

          <StandartDropdown
            v-if="item?.typeHandle === 'navItem'"
            :navItems="item.children"
          />

          <ProductsDropdown
            v-else-if="item?.typeHandle === 'productsDropdown'"
            :parentData="item"
          />

          <DropdownColumns
            v-else-if="item?.typeHandle === 'dropdownColumns'"
            :parentData="item"
          />

        </div>
      </transition>
    </div>
  </nav>
</template>

<!-- -------------------------------------------------------------------------------------------------
    Styling
------------------------------------------------------------------------------------------------- -->

<style scoped>

nav {
  align-items: stretch;
  display: flex;
  height: 72px;
  justify-content: flex-start;
  position: relative;
  z-index: 2;
}

.top-nav-item {
  display: flex;
  margin: 0 12px;
  transition: color .3s ease;
  position: relative;
}

.top-nav-item::after {
  background: currentColor;
  bottom: 0;
  content: "";
  display: block;
  height: 2px;
  left: 0;
  position: absolute;
  transition: all .3s ease-out;
  width: 0;
  z-index: 1;
}

.top-nav-item:hover::after {
  width: 100%;
}

.link, button {
  align-items: center;
  cursor: pointer;
  display: flex;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: -.2px;
  line-height: 22px;
  overflow: hidden;
  padding: 10px 8px;
}

.top-nav-item.has-dropdown button {
  position: relative;
}

.icon-caret {
  font-size: 5px;
  margin-left: 7px;
  position: relative;
  top: -1px;
  width: 5px;
  height: 5px;
  transition: all .3s ease-out;
  transform-origin: center center;
  border-bottom: 1px solid currentColor;
  border-left: 1px solid currentColor;
  transform: rotate(-45deg);
}

.top-nav-item.has-dropdown button[aria-expanded="true"] .icon-caret {
  transform: rotate(-225deg);
}

.dropdown-container {
  position: absolute;
  left: -26px;
  background: var(--color-white);
  z-index: 5;
  transition: all .2s ease;
  top: 72px;
  border-radius: 0 0 16px 16px;
  box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.10);
  overflow: hidden;
}

.dropdown-container:has(.--without-borders) {
  @media (max-width: 1400px) {
    left: -200px;
  }
}

/* Fade transition animation */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from {
  opacity: 0;
}

.fade-enter-to {
  opacity: 1;
}

.fade-leave-from {
  opacity: 1;
}

.fade-leave-to {
  opacity: 0;
}

</style>
